import React, { useEffect, useState } from "react";
import { ChartLegend } from "./ChartLegendTooltip";
import { DateSelector } from "./DateSelector";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  CartesianGrid,
} from "recharts";
import { fetchDeviceChart } from "../api/client";

/* ------------------ */
/* Types              */
/* ------------------ */

export type ChartPoint = {
  time: number; // REGION time (ms)
  price: number;
  heating: number;
  dueMarker: number;
  consumption: number;
};

type HeatingDecisionChartPropsDev = {
  deviceId: number;
};

/* ------------------ */
/* Timezone utils     */
/* ------------------ */

const regionToTimezone: Record<string, string> = {
  ee: "Europe/Tallinn",
  lv: "Europe/Riga",
  lt: "Europe/Vilnius",
  fi: "Europe/Helsinki",
};

const getRegionOffsetMs = (region: string, date: Date): number => {
  const tz = regionToTimezone[region] ?? "UTC";
  const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
  const regionDate = new Date(date.toLocaleString("en-US", { timeZone: tz }));
  return regionDate.getTime() - utcDate.getTime();
};

/* ------------------ */
/* Helpers            */
/* ------------------ */

// Handles both "2026-05-10 12:30:00" and "2026-05-10 12:30:00 UTC"
const parseUtc = (s: string): number =>
  new Date(s.replace(" UTC", "").replace(" ", "T") + "Z").getTime();

/* ------------------ */
/* Component          */
/* ------------------ */

export const HeatingDecisionChartDev: React.FC<
  HeatingDecisionChartPropsDev
> = ({ deviceId }) => {
  const [data, setData] = useState<ChartPoint[]>([]);

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  useEffect(() => {
    if (!deviceId) return;

    const dateStr = `${selectedDate.getFullYear()}-${(
      selectedDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${selectedDate
      .getDate()
      .toString()
      .padStart(2, "0")}`;

    fetchDeviceChart(deviceId, dateStr)
      .then((chart) => {
        const windows = chart.deviceChartData;
        const region = chart.region;

        const now = new Date();
        const tzOffsetMs = getRegionOffsetMs(region, now);

        /* ------------------ */
        /* Region midnight    */
        /* ------------------ */

        const dayMs = 24 * 60 * 60 * 1000;

        /* ------------------ */
        /* Due times          */
        /* ------------------ */

        const toMs = (h: number, m: number, s: number) =>
          (h * 3600 + m * 60 + s) * 1000;

        const dueTimes = new Set<number>(
          chart.dueTimesUtc.map((t: string) => {
            const [hh, mm, ss] = t.split(":").map(Number);
            return toMs(hh, mm, ss) + tzOffsetMs;
          })
        );

        /* ------------------ */
        /* Build chart data   */
        /* ------------------ */

        const chartData: ChartPoint[] = chart.slotPrices.map((p: any) => {
          const tUtc = parseUtc(p.timestampUtc);
          const tRegion = tUtc + tzOffsetMs;

          const window = windows.find((w: any) => {
            const start = parseUtc(w.startUtc) + tzOffsetMs;
            const end = parseUtc(w.endUtc) + tzOffsetMs;
            return tRegion >= start && tRegion < end;
          });

          const tFromMidnight = tRegion % dayMs;

          return {
            time: tRegion,
            price: p.totalPrice,
            heating: window ? 1 : 0,
            dueMarker: dueTimes.has(tFromMidnight) ? 1 : 0,
            consumption: window?.windowConsumption ?? 0,
          };
        });

        if (chartData.length > 0) {
          const last = chartData[chartData.length - 1];
          chartData.push({
            ...last,
            time: last.time + 15 * 60 * 1000,
            heating: 0,
            dueMarker: 0,
          });
        }

        setData(chartData);
      })
      
      .catch((err) => console.error("Failed to fetch device chart:", err));
  }, [deviceId, selectedDate]);

  /* ------------------ */
  /* Render             */
  /* ------------------ */

  const now = new Date();
  const tzOffsetMs = getRegionOffsetMs("ee", now); // safe default, replaced dynamically in data
  const dayMs = 24 * 60 * 60 * 1000;

  const regionMidnightUtc =
    Date.UTC(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0
    ) - tzOffsetMs;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={Array.from({ length: 24 }, (_, h) => {
                return regionMidnightUtc + tzOffsetMs + h * 3600000;
              })}
              tick={{ fontSize: 13, fill: "#374151" }}
              tickFormatter={(ts) => {
                const h = Math.floor((ts % dayMs) / 3600000);
                return h.toString().padStart(2, "0") + ":00";
              }}
            />

            <YAxis
              yAxisId="price"
              orientation="left"
              width={80}
              domain={["auto", "auto"]}
              axisLine
              tickLine
              tick={{ fontSize: 13, fill: "#374151" }}
              tickFormatter={(v) => `${(v / 10).toFixed(0)} c€/kWh`}
            />

            <YAxis
              yAxisId="consumption"
              orientation="right"
              width={80}
              domain={["auto", "auto"]}
              axisLine
              tickLine
              tick={{ fontSize: 13, fill: "#374151" }}
              tickFormatter={(v) => `${(v / 1000).toFixed(2)} kWh`}
            />

            <CartesianGrid
              stroke="#ccc"
              strokeDasharray="3 3"
              vertical={false}
              yAxisId="price"
            />

            <Tooltip content={<ChartLegend />} />

            <Line
              yAxisId="price"
              dataKey="price"
              type="stepAfter"
              stroke="#5c8efa"
              strokeWidth={1}
              dot={false}
            />

            <Area
              yAxisId="heat"
              dataKey="heating"
              type="stepAfter"
              fill="rgba(59,130,246,0.15)"
              stroke="rgba(59,130,246,0.5)"
              dot={false}
            />

            <Area
              yAxisId="consumption"
              dataKey="consumption"
              type="stepAfter"
              fill="rgba(130,110,175,0.4)"
              stroke="rgba(128,0,255,0.88)"
              dot={false}
            />

            <Bar yAxisId="marker" dataKey="dueMarker" fill="red" barSize={3} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <DateSelector
        selectedDate={selectedDate}
        onChange={(d) => setSelectedDate(d)}
      />
    </div>
  );
};